import styled, { css } from "styled-components";
import { Colors, FlexBox } from "@brg/ui";
import { LayoutConst } from "core";

// --- Page layout ---

export const PageWrapper = styled(FlexBox)`
    flex-flow: column;
    gap: 32px;
    padding-top: 16px;
    padding-bottom: 100px;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack100 : Colors.geoWhite100};

    @media (max-width: ${LayoutConst.screen.md}px) {
        gap: 24px;
    }

    @media (max-width: ${LayoutConst.screen.s}px) {
        gap: 20px;
        padding-bottom: 220px;
    }
`;

export const Breadcrumb = styled.div`
    font-size: 17px;
    font-weight: 600;
    line-height: 22px;
    letter-spacing: -0.34px;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;

    @media (max-width: ${LayoutConst.screen.md}px) {
        font-size: 14px;
        line-height: 18px;
    }
`;

export const BreadcrumbLink = styled.span`
    cursor: pointer;
    &:hover {
        opacity: 0.7;
    }
`;

export const BreadcrumbSeparator = styled.span`
    user-select: none;
`;

export const BreadcrumbCurrent = styled.span`
    opacity: 0.5;
`;

export const HeaderBlock = styled(FlexBox)`
    flex-flow: column;
    gap: 8px;
`;

export const ContentLayout = styled(FlexBox)`
    gap: 24px;
    align-items: flex-start;

    @media (max-width: ${LayoutConst.screen.md}px) {
        gap: 16px;
    }

    @media (max-width: ${LayoutConst.screen.s}px) {
        flex-flow: column;
        gap: 24px;
        align-items: stretch;
    }
`;

export const Sidebar = styled(FlexBox)`
    flex-flow: column;
    width: 324px;
    flex-shrink: 0;

    @media (max-width: ${LayoutConst.screen.md}px) {
        width: 180px;
    }

    @media (max-width: ${LayoutConst.screen.s}px) {
        width: 100%;
    }
`;

export const SidebarItem = styled(FlexBox)<{ active?: boolean; disabled?: boolean }>`
    font-size: 20px;
    line-height: 25px;
    font-weight: 600;
    padding: 12px 0;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
    border-bottom: 1px solid ${({ theme }) => theme.isDarkText ? Colors.galBlack21 : Colors.geoWhite21};
    cursor: pointer;
    &:hover {
        color: ${({ theme }) => theme.isDarkText ? Colors.galBlack100 : Colors.geoWhite100};
    }
    ${({ active, theme }) => active && css`
        color: ${theme.isDarkText ? Colors.galBlack100 : Colors.geoWhite100};
        border-bottom-color: ${theme.isDarkText ? Colors.galBlack100 : Colors.geoWhite100};
    `}
    ${({ disabled }) => disabled && css`
        pointer-events: none;
        opacity: 0.4;
    `}

    @media (max-width: ${LayoutConst.screen.md}px) {
        font-size: 15px;
        line-height: 20px;
        padding: 8px 0;
    }
`;

export const ContentArea = styled(FlexBox)`
    flex: 1;
    flex-flow: column;
    gap: 8px;
    min-width: 0;

    @media (max-width: ${LayoutConst.screen.md}px) {
        gap: 24px;
    }
`;

export const SectionTitle = styled.div`
    font-size: 30px;
    line-height: 40px;
    font-weight: 600;
    letter-spacing: -0.6px;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack100 : Colors.geoWhite100};

    @media (max-width: ${LayoutConst.screen.md}px) {
        font-size: 24px;
        line-height: 30px;
        letter-spacing: -0.48px;
    }
`;

// --- Form field primitives ---

export const FieldsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;

    @media (max-width: ${LayoutConst.screen.md}px) {
        gap: 24px;
    }
`;

export const FieldWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`;

export const FieldLabelRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

export const FieldLabelLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const FieldLabelText = styled.span`
    font-size: 15px;
    line-height: 20px;
    font-weight: 400;
    letter-spacing: -0.3px;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
    white-space: nowrap;
`;

export const StaticFieldValue = styled.a`
    font-size: 15px;
    line-height: 20px;
    font-weight: 500;
    letter-spacing: -0.3px;
    text-decoration: underline;
    color: #151514;
    word-break: break-word;
    cursor: pointer;
    &:hover {
        opacity: 0.75;
    }
`;

export const HintIcon = styled.div`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 0.75px solid ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 9.75px;
    line-height: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
    letter-spacing: -0.195px;
    cursor: pointer;
`;

export const LangSwitcher = styled.div`
    display: flex;
    align-items: flex-start;
    background: #f2f2f2;
    border: 1px solid #b6b6b5;
    border-radius: 3px;
    overflow: hidden;
    flex-shrink: 0;
`;

export const LangButton = styled.button<{ active?: boolean }>`
    padding: 6px 8px;
    border: none;
    cursor: pointer;
    font-family: Inter, sans-serif;
    font-size: 13px;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: -0.26px;
    white-space: nowrap;
    border-radius: 3px;
    background: ${({ active }) => active ? '#70706f' : 'transparent'};
    color: ${({ active }) => active ? '#fff' : '#70706f'};
`;

export const InputBox = styled.div`
    background: #f2f2f2;
    border-radius: 3px;
    height: 48px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
`;

export const StyledInput = styled.input`
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    font-family: Inter, sans-serif;
    font-size: 15px;
    line-height: 20px;
    font-weight: 400;
    letter-spacing: -0.3px;
    color: #151514;
    min-width: 0;
    &::placeholder {
        color: #bfbfbf;
    }
    &:read-only {
        cursor: default;
        color: #888888;
    }
`;

export const TextareaWrapper = styled.div`
    background: #f2f2f2;
    border-radius: 3px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    box-sizing: border-box;
    min-height: 120px;
`;

export const StyledTextarea = styled.textarea`
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    resize: none;
    font-family: Inter, sans-serif;
    font-size: 15px;
    line-height: 20px;
    font-weight: 400;
    letter-spacing: -0.3px;
    color: #151514;
    width: 100%;
    min-height: 60px;
    &::placeholder {
        color: #bfbfbf;
    }
`;

export const CharCounter = styled.div`
    font-size: 10px;
    line-height: 13px;
    color: #bfbfbf;
    text-align: right;
    align-self: flex-end;
`;

export const RequiredStar = styled.span`
    margin-left: 2px;
    color: ${Colors.redFailed};
`;

export const HelperText = styled.div`
    font-size: 13px;
    line-height: 20px;
    font-weight: 400;
    letter-spacing: -0.26px;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
    white-space: nowrap;
`;

export const DatePickerWrap = styled.div`
    width: 100%;
    .react-datetime-picker {
        display: flex;
        width: 100%;
    }
    .react-datetime-picker--open {
        position: relative;
        z-index: 20;
    }
    .react-datetime-picker__calendar {
        z-index: 20;
    }
    .react-datetime-picker__wrapper {
        background: #f2f2f2;
        border: none;
        border-radius: 3px;
        height: 48px;
        padding: 0 16px;
        font-family: Inter, sans-serif;
        font-size: 15px;
        color: #151514;
    }
    .react-datetime-picker__inputGroup .react-datetime-picker__inputGroup__day,
    .react-datetime-picker__inputGroup .react-datetime-picker__inputGroup__month,
    .react-datetime-picker__inputGroup .react-datetime-picker__inputGroup__leadingZero,
    .react-datetime-picker__inputGroup .react-datetime-picker__inputGroup__year,
    .react-datetime-picker__inputGroup .react-datetime-picker__inputGroup__divider,
    .react-datetime-picker__inputGroup .react-datetime-picker__inputGroup__hour,
    .react-datetime-picker__inputGroup .react-datetime-picker__inputGroup__minute,
    .react-datetime-picker__inputGroup .react-datetime-picker__inputGroup__second {
        color: #151514 !important;
    }
`;

export const PriceFieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
`;

export const PriceInputRow = styled.div`
    display: flex;
    gap: 16px;
    align-items: flex-start;
    width: 100%;
    & > * {
        flex: 1;
        min-width: 0;
    }
`;

export const PriceHint = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.26px;
    color: #909090;
`;

export const PaidCourseRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
    font-size: 15px;
    line-height: 20px;
    color: ${Colors.galBlack100};
    letter-spacing: -0.3px;
`;

export const ConsentRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 15px;
    line-height: 20px;
    color: ${Colors.galBlack100};
    letter-spacing: -0.3px;
    a {
        color: inherit;
        text-decoration: underline;
    }
`;

export const ConsentText = styled.span`
    flex: 1;
`;

export const ContractBlock = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    font-size: 15px;
    line-height: 20px;
    color: ${Colors.galBlack100};
    letter-spacing: -0.3px;

    @media (max-width: ${LayoutConst.screen.s}px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

export const ContractText = styled.div`
    flex: 1;
    ul {
        margin: 8px 0 0;
        padding-left: 20px;
    }
    li {
        margin-bottom: 4px;
    }
`;

export const DateAndDurationRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 16px;
    width: 100%;

    @media (max-width: ${LayoutConst.screen.md}px) {
        gap: 12px;
        grid-template-columns: 1fr;
    }
`;

// --- Discipline tabs ---

export const DisciplineTabsRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-width: 0;
    width: 100%;

    @media (max-width: ${LayoutConst.screen.md}px) {
        flex-wrap: wrap;
        gap: 8px;
    }
`;

export const DisciplineTabsList = styled.div<{ $scrollable?: boolean }>`
    display: flex;
    align-items: center;
    gap: 24px;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;

    ${({ $scrollable }) => $scrollable && css`
        overflow-x: auto;
        overflow-y: hidden;

        @supports not selector(::-webkit-scrollbar) {
            scrollbar-width: thin;
            scrollbar-color: rgba(52, 52, 52, 0.3) transparent;
        }

        &::-webkit-scrollbar {
            height: 4px;
            width: 4px;
        }

        &::-webkit-scrollbar-track,
        &::-webkit-scrollbar-track-piece,
        &::-webkit-scrollbar-corner {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: rgba(52, 52, 52, 0.3);
            border-radius: 2px;
        }

        &::-webkit-scrollbar-button,
        &::-webkit-scrollbar-button:horizontal,
        &::-webkit-scrollbar-button:horizontal:decrement,
        &::-webkit-scrollbar-button:horizontal:increment,
        &::-webkit-scrollbar-button:horizontal:start:decrement,
        &::-webkit-scrollbar-button:horizontal:start:increment,
        &::-webkit-scrollbar-button:horizontal:end:decrement,
        &::-webkit-scrollbar-button:horizontal:end:increment,
        &::-webkit-scrollbar-button:single-button,
        &::-webkit-scrollbar-button:double-button {
            display: none;
            width: 0;
            height: 0;
            background: transparent;
            border: none;
        }
    `}
`;


export const DisciplineTab = styled.button<{ active?: boolean }>`
    background: none;
    border: none;
    outline: none;
    padding: 4px 0;
    font-family: Inter, sans-serif;
    font-size: 17px;
    line-height: 22px;
    letter-spacing: -0.34px;
    font-weight: 600;
    color: ${({ active, theme }) => active
        ? (theme.isDarkText ? Colors.galBlack100 : Colors.geoWhite100)
        : (theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47)};
    cursor: pointer;
    white-space: nowrap;
    &:focus {
        outline: none;
    }
`;

export const AddDisciplineBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: 1px solid ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
    border-radius: 5px;
    cursor: pointer;
    font-family: Inter, sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
    padding: 5px 12px;
    white-space: nowrap;
    flex-shrink: 0;
    &:disabled {
        opacity: 0.4;
        cursor: default;
    }
`;

export const MobileDisciplineRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
`;

export const MobileDisciplineSelector = styled.button`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    background: #f2f2f2;
    border: none;
    border-radius: 3px;
    height: 40px;
    padding: 0 12px;
    font-family: Inter, sans-serif;
    font-size: 15px;
    color: #151514;
    cursor: pointer;
    overflow: hidden;

    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

// --- Discipline panel ---

export const DisciplinePanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
`;

export const SectionMetaRow = styled.div`
    display: flex;
    gap: 32px;
    align-items: flex-start;

    @media (max-width: ${LayoutConst.screen.md}px) {
        flex-direction: column;
        gap: 16px;
    }
`;

export const SectionNameBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-width: 0;
`;

export const SectionColorBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex-shrink: 0;

    @media (max-width: ${LayoutConst.screen.md}px) {
        width: 100%;
    }
`;

export const SectionLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    line-height: 16px;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
`;

export const SectionNameInput = styled.input`
    background: #f2f2f2;
    border: none;
    border-radius: 3px;
    height: 40px;
    padding: 0 12px;
    font-family: Inter, sans-serif;
    font-size: 15px;
    color: #151514;
    outline: none;
    width: 100%;
    box-sizing: border-box;
    &::placeholder { color: #bfbfbf; }
`;

export const ColorSwatchesRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
`;

export const ColorSwatch = styled.button<{ color: string; active?: boolean }>`
    width: 32px;
    height: 32px;
    border-radius: 3px;
    background: ${({ color }) => color};
    border: 2px solid ${({ active }) => active ? '#545453' : '#BFBFBF'};
    outline: none;
    cursor: pointer;
    flex-shrink: 0;
    padding: 0;
    transition: border-color 0.15s;
    &:focus, &:focus-visible { outline: none; }
`;

// --- Module list ---

export const ModuleList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

export const ModuleCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    background: ${Colors.galBlack5};
    border-radius: 4px;
    overflow: hidden;
    padding: 24px;

    @media (max-width: ${LayoutConst.screen.md}px) {
        padding: 16px;
        gap: 16px;
    }
`;

export const ModuleHeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 8px 12px;

    @media (max-width: ${LayoutConst.screen.md}px) {
        padding: 4px 8px;
    }
`;

export const ModuleHeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

export const ModuleBodyRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;

    @media (max-width: ${LayoutConst.screen.md}px) {
        flex-wrap: wrap;
        padding: 4px 8px;
    }

    @media (max-width: ${LayoutConst.screen.s}px) {
        flex-direction: column;
        align-items: stretch;
        padding: 4px 8px;
        gap: 8px;
    }
`;

export const ModuleConfigureBtn = styled.div`
    flex-shrink: 0;

    @media (max-width: ${LayoutConst.screen.s}px) {
        button {
            width: 100%;
        }
    }
`;

export const DragHandle = styled.span`
    cursor: grab;
`;

export const ModuleTitle = styled.span`
    font-size: 15px;
    line-height: 20px;
    font-weight: 600;
    color: ${Colors.galBlack100};
`;

export const ModuleStatusBadge = styled.span`
    font-family: Inter, sans-serif;
    font-size: 11px;
    line-height: 14px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 3px;
    background: ${Colors.galBlack31};
    color: ${Colors.white};
    white-space: nowrap;
    margin-left: auto;
`;

export const ModuleNameInput = styled.input`
    flex: 1;
    background: #ffffff;
    border: none;
    border-radius: 3px;
    height: 40px;
    padding: 0 12px;
    font-family: Inter, sans-serif;
    font-size: 15px;
    color: #151514;
    outline: none;
    min-width: 0;
    &::placeholder { color: #bfbfbf; }

    @media (max-width: ${LayoutConst.screen.md}px) {
        width: 100%;
    }
`;

export const CollapseBtn = styled.button`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    flex-shrink: 0;
`;

export const ModuleInfoToggle = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    cursor: pointer;
    font-family: Inter, sans-serif;
    font-size: 13px;
    line-height: 20px;
    color: ${Colors.galBlack47};
    padding: 4px 12px;
    text-align: left;
`;

export const ModuleInfoSection = styled.div`
    padding: 4px 12px 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const ModuleInfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    line-height: 20px;
    color: ${Colors.galBlack47};
`;

export const ThemesSection = styled.div`
    padding: 8px 12px 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    border-top: 1px solid ${Colors.galBlack5};
`;

export const ThemesLabel = styled.div`
    font-size: 13px;
    line-height: 20px;
    color: ${Colors.galBlack47};
    margin-bottom: 2px;
`;

export const ThemeRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const ThemeInput = styled.input`
    flex: 1;
    background: #ffffff;
    border: none;
    border-radius: 3px;
    height: 36px;
    padding: 0 12px;
    font-family: Inter, sans-serif;
    font-size: 14px;
    color: #151514;
    outline: none;
    min-width: 0;
    &::placeholder { color: #bfbfbf; }
`;

export const AddLineBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    cursor: pointer;
    font-family: Inter, sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: ${Colors.galBlack47};
    padding: 4px 0;
    svg path { fill: ${Colors.galBlack47}; }

    @media (max-width: ${LayoutConst.screen.s}px) {
        width: 100%;
        justify-content: center;
        border: 1px solid #909090;
        border-radius: 3px;
        padding: 8px 12px;
        color: ${Colors.galBlack100};
        svg path { fill: ${Colors.galBlack100}; }
    }
`;

export const DatesRow = styled.div`
    display: flex;
    gap: 24px;
    align-items: flex-start;
    width: 100%;
    & > * {
        flex: 1;
        min-width: 0;
    }
`;

// --- Description section ---

export const DescriptionBlock = styled(FlexBox)`
    flex-flow: column;
    gap: 24px;
    width: 100%;
`;


export const DescriptionBlockText = styled.div`
    font-size: 17px;
    line-height: 22px;
    font-weight: 400;
    letter-spacing: -0.3px;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack100 : Colors.geoWhite47};
`;

export const CertificateInfoBlock = styled(FlexBox)`
    flex-flow: column;
    gap: 12px;
    width: 100%;
    margin-top: 16px;
`;

export const CertificateInfoRow = styled(FlexBox)`
    align-items: flex-start;
    width: 100%;
`;

export const CertificateInfoLabel = styled(DescriptionBlockText)`
    flex-shrink: 0;
    width: 150px;
`;

export const CertificateInfoValue = styled(DescriptionBlockText)`
    flex: 1;
    min-width: 0;
`;

export const CertificateTemplateButtonWrap = styled(FlexBox)`
    margin-top: 24px;
    width: 100%;
`;

export const CertificateSwitcherWrap = styled(FlexBox)`
    margin-top: 24px;
    width: 100%;
`;

export const CertificateReleasedButtonWrap = styled(FlexBox)`
    margin-top: 16px;
    width: 100%;
`;

export const DescriptionBlockButtonWrap = styled(FlexBox)`
    align-items: center;
    gap: 8px;
    margin-top: 8px;

    @media (max-width: ${LayoutConst.screen.s}px) {
        width: 100%;
        > * {
            width: 100%;
        }
    }
`;

// --- Course edit panel ---

export const CourseEditPanelOuter = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 1000000;
    background-color: ${Colors.galBlack5};
    border-top: 1px solid ${Colors.galBlack10};
`;

export const CourseEditPanelInner = styled(FlexBox)`
    width: 100%;
    max-width: 1154px;
    margin: 0 auto;
    padding: 16px 24px;
    flex-flow: row;
    justify-content: space-between;
    align-items: center;

    @media (max-width: ${LayoutConst.screen.md}px) {
        padding: 12px 16px;
        gap: 16px;
    }

    @media (max-width: ${LayoutConst.screen.s}px) {
        flex-flow: column;
        align-items: stretch;
        gap: 12px;
    }
`;

export const CourseEditPanelLeft = styled(FlexBox)`
    flex-flow: column;
    gap: 4px;
`;

export const CourseEditPanelBackRow = styled(FlexBox)`
    flex-flow: row;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    width: fit-content;
`;

export const CourseEditPanelBackText = styled.span`
    font-size: 15px;
    line-height: 20px;
    font-weight: 600;
    color: ${Colors.galBlack47};
    letter-spacing: -0.3px;
`;

export const CourseEditPanelTitle = styled.div`
    font-size: 17px;
    line-height: 22px;
    font-weight: 600;
    color: ${Colors.galBlack100};
    letter-spacing: -0.34px;
`;

export const CourseEditPanelButtons = styled(FlexBox)`
    flex-flow: row;
    gap: 16px;
    align-items: center;

    @media (max-width: ${LayoutConst.screen.s}px) {
        flex-flow: column;
        align-items: stretch;
        gap: 8px;
        width: 100%;
        > * {
            width: 100%;
        }
    }
`;

export const ScheduleMain = styled(FlexBox)`
    gap: 16px;
`

export const ScheduleContentBlock = styled(FlexBox)`
    gap: 16px;
`

export const StudentDataRow = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    padding: 12px 0;
    gap: 24px;
    align-items: center;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack100 : Colors.geoWhite100};
    font-family: Inter;
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.3px;
    border-bottom: 1px solid ${({ theme }) => theme.isDarkText ? Colors.galBlack21 : Colors.geoWhite21};

    @media (max-width: ${LayoutConst.screen.s}px) {
        gap: 12px;
        align-items: flex-start;
    }
`import styled, { css } from "styled-components";
import { Colors, FlexBox } from "@brg/ui";
import { LayoutConst } from "core";

// --- Page layout ---

export const PageWrapper = styled(FlexBox)`
    flex-flow: column;
    gap: 32px;
    padding-top: 16px;
    padding-bottom: 100px;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack100 : Colors.geoWhite100};

    @media (max-width: ${LayoutConst.screen.md}px) {
        gap: 24px;
    }

    @media (max-width: ${LayoutConst.screen.s}px) {
        gap: 20px;
        padding-bottom: 220px;
    }
`;

export const Breadcrumb = styled.div`
    font-size: 17px;
    font-weight: 600;
    line-height: 22px;
    letter-spacing: -0.34px;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;

    @media (max-width: ${LayoutConst.screen.md}px) {
        font-size: 14px;
        line-height: 18px;
    }
`;

export const BreadcrumbLink = styled.span`
    cursor: pointer;
    &:hover {
        opacity: 0.7;
    }
`;

export const BreadcrumbSeparator = styled.span`
    user-select: none;
`;

export const BreadcrumbCurrent = styled.span`
    opacity: 0.5;
`;

export const HeaderBlock = styled(FlexBox)`
    flex-flow: column;
    gap: 8px;
`;

export const ContentLayout = styled(FlexBox)`
    gap: 24px;
    align-items: flex-start;

    @media (max-width: ${LayoutConst.screen.md}px) {
        gap: 16px;
    }

    @media (max-width: ${LayoutConst.screen.s}px) {
        flex-flow: column;
        gap: 24px;
        align-items: stretch;
    }
`;

export const Sidebar = styled(FlexBox)`
    flex-flow: column;
    width: 324px;
    flex-shrink: 0;

    @media (max-width: ${LayoutConst.screen.md}px) {
        width: 180px;
    }

    @media (max-width: ${LayoutConst.screen.s}px) {
        width: 100%;
    }
`;

export const SidebarItem = styled(FlexBox)<{ active?: boolean; disabled?: boolean }>`
    font-size: 20px;
    line-height: 25px;
    font-weight: 600;
    padding: 12px 0;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
    border-bottom: 1px solid ${({ theme }) => theme.isDarkText ? Colors.galBlack21 : Colors.geoWhite21};
    cursor: pointer;
    &:hover {
        color: ${({ theme }) => theme.isDarkText ? Colors.galBlack100 : Colors.geoWhite100};
    }
    ${({ active, theme }) => active && css`
        color: ${theme.isDarkText ? Colors.galBlack100 : Colors.geoWhite100};
        border-bottom-color: ${theme.isDarkText ? Colors.galBlack100 : Colors.geoWhite100};
    `}
    ${({ disabled }) => disabled && css`
        pointer-events: none;
        opacity: 0.4;
    `}

    @media (max-width: ${LayoutConst.screen.md}px) {
        font-size: 15px;
        line-height: 20px;
        padding: 8px 0;
    }
`;

export const ContentArea = styled(FlexBox)`
    flex: 1;
    flex-flow: column;
    gap: 8px;
    min-width: 0;

    @media (max-width: ${LayoutConst.screen.md}px) {
        gap: 24px;
    }
`;

export const SectionTitle = styled.div`
    font-size: 30px;
    line-height: 40px;
    font-weight: 600;
    letter-spacing: -0.6px;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack100 : Colors.geoWhite100};

    @media (max-width: ${LayoutConst.screen.md}px) {
        font-size: 24px;
        line-height: 30px;
        letter-spacing: -0.48px;
    }
`;

// --- Form field primitives ---

export const FieldsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;

    @media (max-width: ${LayoutConst.screen.md}px) {
        gap: 24px;
    }
`;

export const FieldWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`;

export const FieldLabelRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

export const FieldLabelLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const FieldLabelText = styled.span`
    font-size: 15px;
    line-height: 20px;
    font-weight: 400;
    letter-spacing: -0.3px;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
    white-space: nowrap;
`;

export const StaticFieldValue = styled.a`
    font-size: 15px;
    line-height: 20px;
    font-weight: 500;
    letter-spacing: -0.3px;
    text-decoration: underline;
    color: #151514;
    word-break: break-word;
    cursor: pointer;
    &:hover {
        opacity: 0.75;
    }
`;

export const HintIcon = styled.div`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 0.75px solid ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 9.75px;
    line-height: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
    letter-spacing: -0.195px;
    cursor: pointer;
`;

export const LangSwitcher = styled.div`
    display: flex;
    align-items: flex-start;
    background: #f2f2f2;
    border: 1px solid #b6b6b5;
    border-radius: 3px;
    overflow: hidden;
    flex-shrink: 0;
`;

export const LangButton = styled.button<{ active?: boolean }>`
    padding: 6px 8px;
    border: none;
    cursor: pointer;
    font-family: Inter, sans-serif;
    font-size: 13px;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: -0.26px;
    white-space: nowrap;
    border-radius: 3px;
    background: ${({ active }) => active ? '#70706f' : 'transparent'};
    color: ${({ active }) => active ? '#fff' : '#70706f'};
`;

export const InputBox = styled.div`
    background: #f2f2f2;
    border-radius: 3px;
    height: 48px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
`;

export const StyledInput = styled.input`
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    font-family: Inter, sans-serif;
    font-size: 15px;
    line-height: 20px;
    font-weight: 400;
    letter-spacing: -0.3px;
    color: #151514;
    min-width: 0;
    &::placeholder {
        color: #bfbfbf;
    }
    &:read-only {
        cursor: default;
        color: #888888;
    }
`;

export const TextareaWrapper = styled.div`
    background: #f2f2f2;
    border-radius: 3px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    box-sizing: border-box;
    min-height: 120px;
`;

export const StyledTextarea = styled.textarea`
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    resize: none;
    font-family: Inter, sans-serif;
    font-size: 15px;
    line-height: 20px;
    font-weight: 400;
    letter-spacing: -0.3px;
    color: #151514;
    width: 100%;
    min-height: 60px;
    &::placeholder {
        color: #bfbfbf;
    }
`;

export const CharCounter = styled.div`
    font-size: 10px;
    line-height: 13px;
    color: #bfbfbf;
    text-align: right;
    align-self: flex-end;
`;

export const RequiredStar = styled.span`
    margin-left: 2px;
    color: ${Colors.redFailed};
`;

export const HelperText = styled.div`
    font-size: 13px;
    line-height: 20px;
    font-weight: 400;
    letter-spacing: -0.26px;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
    white-space: nowrap;
`;

export const DatePickerWrap = styled.div`
    width: 100%;
    .react-datetime-picker {
        display: flex;
        width: 100%;
    }
    .react-datetime-picker--open {
        position: relative;
        z-index: 20;
    }
    .react-datetime-picker__calendar {
        z-index: 20;
    }
    .react-datetime-picker__wrapper {
        background: #f2f2f2;
        border: none;
        border-radius: 3px;
        height: 48px;
        padding: 0 16px;
        font-family: Inter, sans-serif;
        font-size: 15px;
        color: #151514;
    }
    .react-datetime-picker__inputGroup .react-datetime-picker__inputGroup__day,
    .react-datetime-picker__inputGroup .react-datetime-picker__inputGroup__month,
    .react-datetime-picker__inputGroup .react-datetime-picker__inputGroup__leadingZero,
    .react-datetime-picker__inputGroup .react-datetime-picker__inputGroup__year,
    .react-datetime-picker__inputGroup .react-datetime-picker__inputGroup__divider,
    .react-datetime-picker__inputGroup .react-datetime-picker__inputGroup__hour,
    .react-datetime-picker__inputGroup .react-datetime-picker__inputGroup__minute,
    .react-datetime-picker__inputGroup .react-datetime-picker__inputGroup__second {
        color: #151514 !important;
    }
`;

export const PriceFieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
`;

export const PriceInputRow = styled.div`
    display: flex;
    gap: 16px;
    align-items: flex-start;
    width: 100%;
    & > * {
        flex: 1;
        min-width: 0;
    }
`;

export const PriceHint = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.26px;
    color: #909090;
`;

export const PaidCourseRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
    font-size: 15px;
    line-height: 20px;
    color: ${Colors.galBlack100};
    letter-spacing: -0.3px;
`;

export const ConsentRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 15px;
    line-height: 20px;
    color: ${Colors.galBlack100};
    letter-spacing: -0.3px;
    a {
        color: inherit;
        text-decoration: underline;
    }
`;

export const ConsentText = styled.span`
    flex: 1;
`;

export const ContractBlock = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    font-size: 15px;
    line-height: 20px;
    color: ${Colors.galBlack100};
    letter-spacing: -0.3px;

    @media (max-width: ${LayoutConst.screen.s}px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

export const ContractText = styled.div`
    flex: 1;
    ul {
        margin: 8px 0 0;
        padding-left: 20px;
    }
    li {
        margin-bottom: 4px;
    }
`;

export const DateAndDurationRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 16px;
    width: 100%;

    @media (max-width: ${LayoutConst.screen.md}px) {
        gap: 12px;
        grid-template-columns: 1fr;
    }
`;

// --- Discipline tabs ---

export const DisciplineTabsRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-width: 0;
    width: 100%;

    @media (max-width: ${LayoutConst.screen.md}px) {
        flex-wrap: wrap;
        gap: 8px;
    }
`;

export const DisciplineTabsList = styled.div<{ $scrollable?: boolean }>`
    display: flex;
    align-items: center;
    gap: 24px;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;

    ${({ $scrollable }) => $scrollable && css`
        overflow-x: auto;
        overflow-y: hidden;

        @supports not selector(::-webkit-scrollbar) {
            scrollbar-width: thin;
            scrollbar-color: rgba(52, 52, 52, 0.3) transparent;
        }

        &::-webkit-scrollbar {
            height: 4px;
            width: 4px;
        }

        &::-webkit-scrollbar-track,
        &::-webkit-scrollbar-track-piece,
        &::-webkit-scrollbar-corner {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: rgba(52, 52, 52, 0.3);
            border-radius: 2px;
        }

        &::-webkit-scrollbar-button,
        &::-webkit-scrollbar-button:horizontal,
        &::-webkit-scrollbar-button:horizontal:decrement,
        &::-webkit-scrollbar-button:horizontal:increment,
        &::-webkit-scrollbar-button:horizontal:start:decrement,
        &::-webkit-scrollbar-button:horizontal:start:increment,
        &::-webkit-scrollbar-button:horizontal:end:decrement,
        &::-webkit-scrollbar-button:horizontal:end:increment,
        &::-webkit-scrollbar-button:single-button,
        &::-webkit-scrollbar-button:double-button {
            display: none;
            width: 0;
            height: 0;
            background: transparent;
            border: none;
        }
    `}
`;


export const DisciplineTab = styled.button<{ active?: boolean }>`
    background: none;
    border: none;
    outline: none;
    padding: 4px 0;
    font-family: Inter, sans-serif;
    font-size: 17px;
    line-height: 22px;
    letter-spacing: -0.34px;
    font-weight: 600;
    color: ${({ active, theme }) => active
        ? (theme.isDarkText ? Colors.galBlack100 : Colors.geoWhite100)
        : (theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47)};
    cursor: pointer;
    white-space: nowrap;
    &:focus {
        outline: none;
    }
`;

export const AddDisciplineBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: 1px solid ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
    border-radius: 5px;
    cursor: pointer;
    font-family: Inter, sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
    padding: 5px 12px;
    white-space: nowrap;
    flex-shrink: 0;
    &:disabled {
        opacity: 0.4;
        cursor: default;
    }
`;

export const MobileDisciplineRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
`;

export const MobileDisciplineSelector = styled.button`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    background: #f2f2f2;
    border: none;
    border-radius: 3px;
    height: 40px;
    padding: 0 12px;
    font-family: Inter, sans-serif;
    font-size: 15px;
    color: #151514;
    cursor: pointer;
    overflow: hidden;

    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

// --- Discipline panel ---

export const DisciplinePanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
`;

export const SectionMetaRow = styled.div`
    display: flex;
    gap: 32px;
    align-items: flex-start;

    @media (max-width: ${LayoutConst.screen.md}px) {
        flex-direction: column;
        gap: 16px;
    }
`;

export const SectionNameBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-width: 0;
`;

export const SectionColorBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex-shrink: 0;

    @media (max-width: ${LayoutConst.screen.md}px) {
        width: 100%;
    }
`;

export const SectionLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    line-height: 16px;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack47 : Colors.geoWhite47};
`;

export const SectionNameInput = styled.input`
    background: #f2f2f2;
    border: none;
    border-radius: 3px;
    height: 40px;
    padding: 0 12px;
    font-family: Inter, sans-serif;
    font-size: 15px;
    color: #151514;
    outline: none;
    width: 100%;
    box-sizing: border-box;
    &::placeholder { color: #bfbfbf; }
`;

export const ColorSwatchesRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
`;

export const ColorSwatch = styled.button<{ color: string; active?: boolean }>`
    width: 32px;
    height: 32px;
    border-radius: 3px;
    background: ${({ color }) => color};
    border: 2px solid ${({ active }) => active ? '#545453' : '#BFBFBF'};
    outline: none;
    cursor: pointer;
    flex-shrink: 0;
    padding: 0;
    transition: border-color 0.15s;
    &:focus, &:focus-visible { outline: none; }
`;

// --- Module list ---

export const ModuleList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

export const ModuleCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    background: ${Colors.galBlack5};
    border-radius: 4px;
    overflow: hidden;
    padding: 24px;

    @media (max-width: ${LayoutConst.screen.md}px) {
        padding: 16px;
        gap: 16px;
    }
`;

export const ModuleHeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 8px 12px;

    @media (max-width: ${LayoutConst.screen.md}px) {
        padding: 4px 8px;
    }
`;

export const ModuleHeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

export const ModuleBodyRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;

    @media (max-width: ${LayoutConst.screen.md}px) {
        flex-wrap: wrap;
        padding: 4px 8px;
    }

    @media (max-width: ${LayoutConst.screen.s}px) {
        flex-direction: column;
        align-items: stretch;
        padding: 4px 8px;
        gap: 8px;
    }
`;

export const ModuleConfigureBtn = styled.div`
    flex-shrink: 0;

    @media (max-width: ${LayoutConst.screen.s}px) {
        button {
            width: 100%;
        }
    }
`;

export const DragHandle = styled.span`
    cursor: grab;
`;

export const ModuleTitle = styled.span`
    font-size: 15px;
    line-height: 20px;
    font-weight: 600;
    color: ${Colors.galBlack100};
`;

export const ModuleStatusBadge = styled.span`
    font-family: Inter, sans-serif;
    font-size: 11px;
    line-height: 14px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 3px;
    background: ${Colors.galBlack31};
    color: ${Colors.white};
    white-space: nowrap;
    margin-left: auto;
`;

export const ModuleNameInput = styled.input`
    flex: 1;
    background: #ffffff;
    border: none;
    border-radius: 3px;
    height: 40px;
    padding: 0 12px;
    font-family: Inter, sans-serif;
    font-size: 15px;
    color: #151514;
    outline: none;
    min-width: 0;
    &::placeholder { color: #bfbfbf; }

    @media (max-width: ${LayoutConst.screen.md}px) {
        width: 100%;
    }
`;

export const CollapseBtn = styled.button`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    flex-shrink: 0;
`;

export const ModuleInfoToggle = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    cursor: pointer;
    font-family: Inter, sans-serif;
    font-size: 13px;
    line-height: 20px;
    color: ${Colors.galBlack47};
    padding: 4px 12px;
    text-align: left;
`;

export const ModuleInfoSection = styled.div`
    padding: 4px 12px 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const ModuleInfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    line-height: 20px;
    color: ${Colors.galBlack47};
`;

export const ThemesSection = styled.div`
    padding: 8px 12px 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    border-top: 1px solid ${Colors.galBlack5};
`;

export const ThemesLabel = styled.div`
    font-size: 13px;
    line-height: 20px;
    color: ${Colors.galBlack47};
    margin-bottom: 2px;
`;

export const ThemeRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const ThemeInput = styled.input`
    flex: 1;
    background: #ffffff;
    border: none;
    border-radius: 3px;
    height: 36px;
    padding: 0 12px;
    font-family: Inter, sans-serif;
    font-size: 14px;
    color: #151514;
    outline: none;
    min-width: 0;
    &::placeholder { color: #bfbfbf; }
`;

export const AddLineBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    cursor: pointer;
    font-family: Inter, sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: ${Colors.galBlack47};
    padding: 4px 0;
    svg path { fill: ${Colors.galBlack47}; }

    @media (max-width: ${LayoutConst.screen.s}px) {
        width: 100%;
        justify-content: center;
        border: 1px solid #909090;
        border-radius: 3px;
        padding: 8px 12px;
        color: ${Colors.galBlack100};
        svg path { fill: ${Colors.galBlack100}; }
    }
`;

export const DatesRow = styled.div`
    display: flex;
    gap: 24px;
    align-items: flex-start;
    width: 100%;
    & > * {
        flex: 1;
        min-width: 0;
    }
`;

// --- Description section ---

export const DescriptionBlock = styled(FlexBox)`
    flex-flow: column;
    gap: 24px;
    width: 100%;
`;


export const DescriptionBlockText = styled.div`
    font-size: 17px;
    line-height: 22px;
    font-weight: 400;
    letter-spacing: -0.3px;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack100 : Colors.geoWhite47};
`;

export const CertificateInfoBlock = styled(FlexBox)`
    flex-flow: column;
    gap: 12px;
    width: 100%;
    margin-top: 16px;
`;

export const CertificateInfoRow = styled(FlexBox)`
    align-items: flex-start;
    width: 100%;
`;

export const CertificateInfoLabel = styled(DescriptionBlockText)`
    flex-shrink: 0;
    width: 150px;
`;

export const CertificateInfoValue = styled(DescriptionBlockText)`
    flex: 1;
    min-width: 0;
`;

export const CertificateTemplateButtonWrap = styled(FlexBox)`
    margin-top: 24px;
    width: 100%;
`;

export const CertificateSwitcherWrap = styled(FlexBox)`
    margin-top: 24px;
    width: 100%;
`;

export const CertificateReleasedButtonWrap = styled(FlexBox)`
    margin-top: 16px;
    width: 100%;
`;

export const DescriptionBlockButtonWrap = styled(FlexBox)`
    align-items: center;
    gap: 8px;
    margin-top: 8px;

    @media (max-width: ${LayoutConst.screen.s}px) {
        width: 100%;
        > * {
            width: 100%;
        }
    }
`;

// --- Course edit panel ---

export const CourseEditPanelOuter = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 1000000;
    background-color: ${Colors.galBlack5};
    border-top: 1px solid ${Colors.galBlack10};
`;

export const CourseEditPanelInner = styled(FlexBox)`
    width: 100%;
    max-width: 1154px;
    margin: 0 auto;
    padding: 16px 24px;
    flex-flow: row;
    justify-content: space-between;
    align-items: center;

    @media (max-width: ${LayoutConst.screen.md}px) {
        padding: 12px 16px;
        gap: 16px;
    }

    @media (max-width: ${LayoutConst.screen.s}px) {
        flex-flow: column;
        align-items: stretch;
        gap: 12px;
    }
`;

export const CourseEditPanelLeft = styled(FlexBox)`
    flex-flow: column;
    gap: 4px;
`;

export const CourseEditPanelBackRow = styled(FlexBox)`
    flex-flow: row;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    width: fit-content;
`;

export const CourseEditPanelBackText = styled.span`
    font-size: 15px;
    line-height: 20px;
    font-weight: 600;
    color: ${Colors.galBlack47};
    letter-spacing: -0.3px;
`;

export const CourseEditPanelTitle = styled.div`
    font-size: 17px;
    line-height: 22px;
    font-weight: 600;
    color: ${Colors.galBlack100};
    letter-spacing: -0.34px;
`;

export const CourseEditPanelButtons = styled(FlexBox)`
    flex-flow: row;
    gap: 16px;
    align-items: center;

    @media (max-width: ${LayoutConst.screen.s}px) {
        flex-flow: column;
        align-items: stretch;
        gap: 8px;
        width: 100%;
        > * {
            width: 100%;
        }
    }
`;

export const ScheduleMain = styled(FlexBox)`
    gap: 16px;
`

export const ScheduleContentBlock = styled(FlexBox)`
    gap: 16px;
`

export const StudentDataRow = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    padding: 12px 0;
    gap: 24px;
    align-items: center;
    color: ${({ theme }) => theme.isDarkText ? Colors.galBlack100 : Colors.geoWhite100};
    font-family: Inter;
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.3px;
    border-bottom: 1px solid ${({ theme }) => theme.isDarkText ? Colors.galBlack21 : Colors.geoWhite21};

    @media (max-width: ${LayoutConst.screen.s}px) {
        gap: 12px;
        align-items: flex-start;
    }
`